import { browserHistory } from "react-router";
import { delay, call, put, select, takeEvery } from "redux-saga/effects";

import * as api from "../../services/employee/api_employee";
import * as apiCommon from "../../services/apiCommon";
import * as common from "../sagasCommon";
import { ENTITY, ENTITY_TYPE } from "../../constants/constant_doc_types";
import { EMPLOYEE } from "../../constants/constant_entity_types";
import * as utilityPreference from "../../services/preference/utilty_preference";
import { attachStandardFieldValuesToCustomForm } from "../../services/apiUtils";
import { getParticularPreferenceSettings } from "../../common/selectors/preference";
import { getPrimarySubsidiary } from "../../common/selectors/selector_common";
import { precheckEntity } from "../sagasCommonEntity";

export function* fetchListEmployeeSaga(feathersApp) {
  yield takeEvery("FETCH_LIST_EMPLOYEES", callListEmployee, feathersApp);
}

function* callListEmployee(feathersApp, action) {
  const { filters, silent } = action;
  yield common.showProgressListTable(silent);
  try {
    const dataParams = {
      filters,
      doc_type: "entity/employee"
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(common.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total
    });
  } catch (error) {
    yield common.showErrorListTable(error);
  }
}

export function* deleteEmployeeFromListSaga(feathersApp) {
  yield takeEvery(
    "DELETE_EMPLOYEE_FROM_LIST",
    callDeleteEmployeeFromList,
    feathersApp
  );
}

function* callDeleteEmployeeFromList(feathersApp, action) {
  yield deleteEmployee(feathersApp, action);
  yield delay(2000);
  yield callListEmployee(feathersApp, { silent: true });
}

function* deleteEmployee(feathersApp, action) {
  yield common.showProgressDeleteRecord();
  try {
    const { id } = action;
    let result = null;
    if (id) {
      const state = yield select();
      const { tenant, user } = state;
      const { id } = action;
      const params = { tenant, user, id };
      result = yield call(api.deleteEmployee, feathersApp, params);
    }
    yield common.doneDeleteRecord(id, ENTITY);
    return result;
  } catch (error) {
    yield common.showErrorDeleteRecord(error);
  }
}

export function* deleteEmployeeSaga(feathersApp) {
  yield takeEvery("DELETE_EMPLOYEE", callDeleteEmployee, feathersApp);
}

function* callDeleteEmployee(feathersApp, action) {
  const result = yield deleteEmployee(feathersApp, action);
  yield delay(2000);
  if (result) {
    browserHistory.push("/admin/employee");
  }
}

export function* fetchEmployeeForEditSaga(feathersApp) {
  yield takeEvery(
    "FETCH_EMPLOYEE_FOR_EDIT",
    callFetchEmployeeForEdit,
    feathersApp
  );
}

function* callFetchEmployeeForEdit(feathersApp, action) {
  const { tenant, user, subsidiary, path } = yield select();
  const { id, silent } = action;
  yield common.showProgressDataById(silent);
  try {
    let data;

    if (id) {
      const fetchParams = { tenant, user, id, path};
      const doc = yield call(
        api.fetchEmployeeForEdit,
        feathersApp,
        fetchParams
      );

      (doc.contact_numbers || []).reverse();
      (doc.address_details || []).reverse();

      doc.custom_fields = yield call(
        attachStandardFieldValuesToCustomForm,
        doc,
        ENTITY,
      );

      data = doc;
    } else {
      const entityTypesParams = { cid: [EMPLOYEE] };
      const entityTypes = yield call(
        apiCommon.queryDocs,
        feathersApp,
        ENTITY_TYPE,
        entityTypesParams
      );
      const accountId = (tenant || {}).id;
      const subsidiaryId =
        subsidiary.primary_subsidiary.id || subsidiary.primary_subsidiary._id;
      const defaultCurrency = subsidiary.primary_subsidiary.currency || {};
      const preferenceParams = {
        accountId,
        subsidiaryId,
        settings: ['default_price_level', 'default_entity_loyalty'],
      };
      const preferences = yield call(
        utilityPreference.getPreferenceValues,
        feathersApp,
        preferenceParams
      );

      const initialValues = {
        entity_type_ids: entityTypes.data.length
          ? [entityTypes.data[0].id]
          : [],
        contact_numbers: [{ primary: true, type: 'home' }],
        active: true,
        employee_access_enabled: true,
        price_level_id: preferences.default_entity_loyalty
          ? preferences.default_price_level
          : undefined,
        preferred_currency_id: defaultCurrency.id,
        preferred_currency: defaultCurrency,
        loyalty_anniversary: preferences.default_entity_loyalty
          ? new Date()
          : undefined,
        loyalty_member: preferences.default_entity_loyalty,
      };

      data = initialValues;
    }

    yield common.doneDataById(data);
  } catch (error) {
    yield common.showErrorDataById(error);
  }
}

export function* saveEmployeeSaga(feathersApp) {
  yield takeEvery("SAVE_EMPLOYEE", callSaveEmployee, feathersApp);
}
function* callSaveEmployee(feathersApp, action) {
  yield common.showProgressSaveRecord();
  const { tenant, user } = yield select();
  try {
    const ok = yield precheckEntity(feathersApp, action);
    if (!ok) {
      yield common.doneSaveRecord();
      return;
    }

    let result = yield call(api.saveEmployee, feathersApp, {
      tenant,
      user,
      data: action.data,
    });

    const id = result && result.id;

    if (id) {
      yield callFetchEmployeeForEdit(feathersApp, { id, silent: true });
      yield put({
        type: "EMPLOYEE_REGISTERPIN_CHANGED",
        register_pin: result.register_pin
      });
      yield put({ type: "DISPLAY_SUBSIDIARY" });
      browserHistory.push(`/admin/employee/edit/${id}`);
      yield common.doneSaveRecord(result, "Employee was saved");
    } else {
      yield common.showErrorSaveRecord(new Error("Error in saving : Employee"));
    }
  } catch (error) {
    yield common.showErrorSaveRecord(error);
  }
}

function callEmployeeResetPw(feathersApp, email) {
  try {
    const resetService = feathersApp.service("auth0/employee");
    return resetService.patch("reset-pw", {email: email})
      .then(response => {
        return Promise.resolve(response);
      });
  } catch (err) {
    console.error(err);
  }

}

export function* changeEmployeePasswordSaga(feathersApp) {
  yield takeEvery("EMPLOYEE_CHANGE_PASS", changeEmployeePassword, feathersApp);
}
export function* changeEmployeePassword(feathersApp, action) {
  const response = yield call(callEmployeeResetPw, feathersApp, action.email);
  yield put({ type: "SAVE_RECORD_DONE", message: response });
  yield put({ type: "EMPLOYEE_PASSWORD_RESET_SENT" });
}

export function* changeEmployeePinSaga(feathersApp) {
  return yield takeEvery("EMPLOYEE_CHANGE_PIN", changeEmployeePin, feathersApp);
}

export function* changeEmployeePin(feathersApp, action) {
  try {
    const { user } = yield select();
    const params = {
      id: action.employeeId,
      user
    };

    const response = yield call(api.resetPin, feathersApp, params);
    yield put({
      type: "EMPLOYEE_REGISTERPIN_CHANGED",
      register_pin: response.register_pin
    });
  } catch (e) {
    console.error("changeEmployeePin", e.stack);
  }
}

export function* emailEmployeePinSaga(feathersApp) {
  yield takeEvery("EMPLOYEE_EMAIL_PIN", callEmailEmployeePin, feathersApp);
}
function* callEmailEmployeePin(feathersApp, action) {
  try {
    const { payload } = action;
    const state = yield select();

    const emailTemplatePinId = getParticularPreferenceSettings(
      state,
      'email_body_template_pin',
      ['value']
    ).value;

    if (!emailTemplatePinId)
      throw new Error(
        'Uh oh, template not found. Kindly check the preference if it has been set'
      );

    const params =  { employee_id: payload, template_id: emailTemplatePinId };
    yield call(api.emailPin, feathersApp, params);

    yield common.showMessageDataById('Email sent');
  } catch (e) {
    console.error("emailPin", e.message || e);
    yield call(common.showErrorSaveRecord, e);
  }
}

export function* smsEmployeePinSaga(feathersApp) {
  yield takeEvery("EMPLOYEE_SMS_PIN", callSmsEmployeePin, feathersApp);
}
function* callSmsEmployeePin(feathersApp, action) {
  try {
    const { payload } = action;
    const state = yield select();
    const { _id: subsidiary } = yield select(getPrimarySubsidiary);

    const smsTemplatePinId = getParticularPreferenceSettings(
      state,
      'sms_template_pin',
      ['value']
    ).value;

    if (!smsTemplatePinId)
      throw new Error(
        'Uh oh, template not found. Kindly check the preference if it has been set'
      );

    const data = { employee_id: payload, template_id: smsTemplatePinId };
    const params = { subsidiary };
    yield call(api.smsPin, feathersApp, data, params);

    yield common.showMessageDataById('Message sent');
  } catch (e) {
    yield call(common.showErrorSaveRecord, e);
  }
}
